'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface SaleFormData {
  lotNumber: string;
  grade: string;
  buyerName: string;
  bags: number;
  weight: number;
  ratePerKg: number;
}

const LOT_OPTIONS = [
  { value: '300', label: 'Lot #300 — Onion (52 bags A, 45 bags B left)' },
  { value: '470', label: 'Lot #470 — Potato (75 bags A, 13 bags B left)' },
  { value: '410', label: 'Lot #410 — Garlic (48 bags A, 22 bags B left)' },
  { value: '330', label: 'Lot #330 — Ginger (30 bags A, 20 bags B left)' },
];

const BUYER_OPTIONS = [
  { name: 'Senthil Traders', outstanding: 12500 },
  { name: 'Murugan Wholesale', outstanding: 0 },
  { name: 'Karthik Enterprises', outstanding: 44000 },
  { name: 'Priya Vegetable Store', outstanding: 32500 },
  { name: 'Anbu Traders', outstanding: 9000 },
  { name: 'Rajan Supermarket', outstanding: 0 },
];

export default function QuickSaleForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState<{ name: string; outstanding: number } | null>(null);
  const [calculatedAmount, setCalculatedAmount] = useState(0);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<SaleFormData>();

  const weight = watch('weight');
  const ratePerKg = watch('ratePerKg');

  React.useEffect(() => {
    if (weight && ratePerKg) {
      setCalculatedAmount(Number(weight) * Number(ratePerKg));
    }
  }, [weight, ratePerKg]);

  const onSubmit = async (data: SaleFormData) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/sales with lot_id, buyer_id, tenant_id
    await new Promise((r) => setTimeout(r, 1000));
    setIsSubmitting(false);
    toast.success(`Sale recorded — Lot #${data.lotNumber} · ₹${calculatedAmount.toLocaleString('en-IN')}`);
    reset();
    setSelectedBuyer(null);
    setCalculatedAmount(0);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Buyer outstanding alert */}
      {selectedBuyer && selectedBuyer.outstanding > 0 && (
        <div className="flex items-center gap-2 bg-warning/10 border border-warning/20 rounded-xl p-3 mb-4">
          <AlertTriangle size={14} className="text-warning flex-shrink-0" />
          <p className="text-xs text-warning font-medium">
            {selectedBuyer.name} has outstanding balance of{' '}
            <span className="font-bold amount-display">₹{selectedBuyer.outstanding.toLocaleString('en-IN')}</span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Lot Number */}
        <div className="col-span-2 sm:col-span-1 lg:col-span-1">
          <label className="block text-2xs font-semibold text-muted-foreground mb-1">
            Lot <span className="text-danger">*</span>
          </label>
          <select
            {...register('lotNumber', { required: 'Required' })}
            className="input-field text-sm py-2.5"
          >
            <option value="">Select lot</option>
            {LOT_OPTIONS.map((opt) => (
              <option key={`lot-opt-${opt.value}`} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {errors.lotNumber && <p className="text-2xs text-danger mt-0.5">{errors.lotNumber.message}</p>}
        </div>

        {/* Grade */}
        <div>
          <label className="block text-2xs font-semibold text-muted-foreground mb-1">
            Grade <span className="text-danger">*</span>
          </label>
          <select
            {...register('grade', { required: 'Required' })}
            className="input-field text-sm py-2.5"
          >
            <option value="">Grade</option>
            <option value="A">A Grade</option>
            <option value="B">B Grade</option>
            <option value="C">C Grade</option>
          </select>
          {errors.grade && <p className="text-2xs text-danger mt-0.5">{errors.grade.message}</p>}
        </div>

        {/* Buyer */}
        <div className="col-span-2 sm:col-span-1">
          <label className="block text-2xs font-semibold text-muted-foreground mb-1">
            Buyer <span className="text-danger">*</span>
          </label>
          <select
            {...register('buyerName', { required: 'Required' })}
            onChange={(e) => {
              const buyer = BUYER_OPTIONS.find((b) => b.name === e.target.value) || null;
              setSelectedBuyer(buyer);
              setValue('buyerName', e.target.value);
            }}
            className="input-field text-sm py-2.5"
          >
            <option value="">Select buyer</option>
            {BUYER_OPTIONS.map((b) => (
              <option key={`buyer-opt-${b.name}`} value={b.name}>{b.name}</option>
            ))}
          </select>
          {errors.buyerName && <p className="text-2xs text-danger mt-0.5">{errors.buyerName.message}</p>}
        </div>

        {/* Bags */}
        <div>
          <label className="block text-2xs font-semibold text-muted-foreground mb-1">
            Bags <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            {...register('bags', { required: 'Required', min: { value: 1, message: 'Min 1' } })}
            placeholder="0"
            className="input-field text-sm py-2.5"
          />
          {errors.bags && <p className="text-2xs text-danger mt-0.5">{errors.bags.message}</p>}
        </div>

        {/* Weight */}
        <div>
          <label className="block text-2xs font-semibold text-muted-foreground mb-1">
            Weight (kg) <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            step="0.1"
            {...register('weight', { required: 'Required', min: { value: 1, message: 'Min 1' } })}
            placeholder="0.0"
            className="input-field text-sm py-2.5"
          />
          {errors.weight && <p className="text-2xs text-danger mt-0.5">{errors.weight.message}</p>}
        </div>

        {/* Rate */}
        <div>
          <label className="block text-2xs font-semibold text-muted-foreground mb-1">
            Rate/kg (₹) <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            step="0.5"
            {...register('ratePerKg', { required: 'Required', min: { value: 0.5, message: 'Min 0.5' } })}
            placeholder="0.00"
            className="input-field text-sm py-2.5"
          />
          {errors.ratePerKg && <p className="text-2xs text-danger mt-0.5">{errors.ratePerKg.message}</p>}
        </div>
      </div>

      {/* Amount preview + submit */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-2xs text-muted-foreground">Calculated Amount</p>
            <p className="text-xl font-bold text-accent amount-display">
              ₹{calculatedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
          </div>
          {calculatedAmount > 0 && (
            <div className="flex items-center gap-1 text-2xs text-accent">
              <CheckCircle size={12} />
              <span>Auto-calculated</span>
            </div>
          )}
        </div>
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving...
            </>
          ) : (
            'Record Sale'
          )}
        </button>
      </div>
    </form>
  );
}